/**
 * 在途监管平台 — 轨迹地图（SVG 简化示意）
 * 静态地图：网格背景 + 轨迹折线 + 起终点标记 + 围栏圆示意。
 * 生产环境可替换为高德 JS API（GCJ-02 坐标系已统一）。
 */
import { useMemo } from 'react';
import type { GPSPoint } from '@/types';

interface TrackMapProps {
  points: GPSPoint[];
  fences?: { lng: number; lat: number; radius_m: number; fence_type: number; fence_name?: string }[];
  width?: number;
  height?: number;
}

export default function TrackMap({ points, fences = [], width = 800, height = 380 }: TrackMapProps) {
  const view = useMemo(() => {
    const all: { lng: number; lat: number }[] = [...points];
    fences.forEach((f) => all.push({ lng: f.lng, lat: f.lat }));
    if (all.length === 0) return null;

    let minLng = Math.min(...all.map((p) => p.lng));
    let maxLng = Math.max(...all.map((p) => p.lng));
    let minLat = Math.min(...all.map((p) => p.lat));
    let maxLat = Math.max(...all.map((p) => p.lat));

    const pad = 0.03;
    minLng -= pad;
    maxLng += pad;
    minLat -= pad;
    maxLat += pad;

    const toX = (lng: number): number => ((lng - minLng) / (maxLng - minLng || 1)) * (width - 40) + 20;
    const toY = (lat: number): number => height - 20 - ((lat - minLat) / (maxLat - minLat || 1)) * (height - 40);

    const linePoints = points.map((p) => `${toX(p.lng)},${toY(p.lat)}`).join(' ');
    const gridLines: { x: number; y: number }[] = [];
    for (let i = 1; i < 5; i++) {
      gridLines.push({ x: (width / 5) * i, y: 0 });
      gridLines.push({ x: 0, y: (height / 5) * i });
    }

    return { toX, toY, linePoints, gridLines, minLng, maxLng, minLat, maxLat };
  }, [points, fences, width, height]);

  if (!view || points.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height,
          borderRadius: 'var(--radius)',
          background: 'var(--color-bg-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-ink-muted)',
        }}
      >
        暂无轨迹数据
      </div>
    );
  }

  const start = points[0];
  const end = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', height: 'auto', borderRadius: 'var(--radius)', background: '#f7fafb' }}
    >
      {/* 网格 */}
      {view.gridLines.map((g, i) => (
        <g key={i}>
          <line x1={g.x} y1={0} x2={g.x} y2={height} stroke="#e6ebed" strokeWidth={1} />
          <line x1={0} y1={g.y} x2={width} y2={g.y} stroke="#e6ebed" strokeWidth={1} />
        </g>
      ))}

      {/* 围栏圆 */}
      {fences.map((f, i) => {
        const cx = view.toX(f.lng);
        const cy = view.toY(f.lat);
        const rPx = Math.max(8, (f.radius_m / 111320) / ((view.maxLat - view.minLat) / (height - 40)) * 10);
        const color = f.fence_type === 1 ? 'var(--color-amber)' : f.fence_type === 3 ? 'var(--color-green)' : 'var(--color-blue)';
        return (
          <g key={i}>
            <circle cx={cx} cy={cy} r={rPx} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="6 4" opacity={0.7} />
            <text x={cx + 6} y={cy - 6} fontSize={10} fill={color}>
              {f.fence_name ?? `围栏${i + 1}`}
            </text>
          </g>
        );
      })}

      {/* 轨迹折线 */}
      <polyline points={view.linePoints} fill="none" stroke="var(--color-blue)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" opacity={0.9} />

      {/* 轨迹点 */}
      {points.map((p, i) => (
        <circle
          key={i}
          cx={view.toX(p.lng)}
          cy={view.toY(p.lat)}
          r={i % 6 === 0 ? 3 : 2}
          fill="var(--color-blue)"
          opacity={0.55}
        />
      ))}

      {/* 起点 */}
      <circle cx={view.toX(start.lng)} cy={view.toY(start.lat)} r={7} fill="var(--color-green)" stroke="#fff" strokeWidth={2} />
      <text x={view.toX(start.lng) + 10} y={view.toY(start.lat) + 4} fontSize={12} fontWeight={700} fill="var(--color-green-dark)">
        起
      </text>

      {/* 终点 */}
      <circle cx={view.toX(end.lng)} cy={view.toY(end.lat)} r={7} fill="var(--color-red)" stroke="#fff" strokeWidth={2} />
      <text x={view.toX(end.lng) + 10} y={view.toY(end.lat) + 4} fontSize={12} fontWeight={700} fill="var(--color-red)">
        终
      </text>
    </svg>
  );
}
